import apiClient from './apiClient';

/**
 * File upload service for handling various file upload operations
 */
export const fileUploadService = {
  // Generic file upload
  uploadFile: async (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    
    // Add additional options
    Object.keys(options).forEach(key => {
      formData.append(key, options[key]);
    });

    return apiClient.upload('/upload', formData, {
      onUploadProgress: options.onProgress
    });
  },

  // Profile image upload
  uploadProfileImage: async (file, userId, options = {}) => {
    const formData = new FormData();
    formData.append('profile_image', file);
    formData.append('user_id', userId);

    return apiClient.upload('/upload/profile-image', formData, {
      onUploadProgress: options.onProgress
    });
  },

  // Aircraft image upload
  uploadAircraftImage: async (file, aircraftId, options = {}) => {
    const formData = new FormData();
    formData.append('aircraft_image', file);
    formData.append('aircraft_id', aircraftId);
    
    if (options.isPrimary) {
      formData.append('is_primary', 'true');
    }

    return apiClient.upload('/upload/aircraft-image', formData, {
      onUploadProgress: options.onProgress
    });
  },

  // Multiple aircraft images upload
  uploadAircraftImages: async (files, aircraftId, options = {}) => {
    const formData = new FormData();
    
    files.forEach((file, index) => {
      formData.append(`aircraft_images[${index}]`, file);
    });
    
    formData.append('aircraft_id', aircraftId);

    return apiClient.upload('/upload/aircraft-images', formData, {
      onUploadProgress: options.onProgress
    });
  },

  // Document upload (AOC, certificates, etc.)
  uploadDocument: async (file, documentType, entityId, options = {}) => {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('document_type', documentType);
    formData.append('entity_id', entityId);
    
    if (options.description) {
      formData.append('description', options.description);
    }

    return apiClient.upload('/upload/document', formData, {
      onUploadProgress: options.onProgress
    });
  },

  // AOC document upload (specific for airliners)
  uploadAOCDocument: async (file, airlinerId, options = {}) => {
    return fileUploadService.uploadDocument(file, 'aoc', airlinerId, {
      ...options,
      description: 'Air Operator Certificate'
    });
  },

  // Certificate upload
  uploadCertificate: async (file, certificateType, entityId, options = {}) => {
    return fileUploadService.uploadDocument(file, `certificate_${certificateType}`, entityId, options);
  },

  // Seating layout image upload
  uploadSeatingLayout: async (file, aircraftId, options = {}) => {
    const formData = new FormData();
    formData.append('seating_layout', file);
    formData.append('aircraft_id', aircraftId);

    return apiClient.upload('/upload/seating-layout', formData, {
      onUploadProgress: options.onProgress
    });
  },

  // Company logo upload
  uploadCompanyLogo: async (file, companyId, options = {}) => {
    const formData = new FormData();
    formData.append('company_logo', file);
    formData.append('company_id', companyId);

    return apiClient.upload('/upload/company-logo', formData, {
      onUploadProgress: options.onProgress
    });
  },

  // Delete uploaded file
  deleteFile: async (fileId) => {
    return apiClient.delete(`/upload/${fileId}`);
  },

  // Get file info
  getFileInfo: async (fileId) => {
    return apiClient.get(`/upload/${fileId}`);
  },

  // Get files by entity
  getFilesByEntity: async (entityType, entityId) => {
    return apiClient.get(`/upload/entity/${entityType}/${entityId}`);
  },

  // Update file metadata
  updateFileMetadata: async (fileId, metadata) => {
    return apiClient.put(`/upload/${fileId}/metadata`, metadata);
  },

  // Batch delete files
  deleteFiles: async (fileIds) => {
    return apiClient.post('/upload/batch-delete', { file_ids: fileIds });
  },

  // Get upload progress
  getUploadProgress: async (uploadId) => {
    return apiClient.get(`/upload/progress/${uploadId}`);
  },

  // Cancel upload
  cancelUpload: async (uploadId) => {
    return apiClient.post(`/upload/cancel/${uploadId}`);
  },

  // Validate file before upload
  validateFile: (file, options = {}) => {
    const {
      maxSize = 10 * 1024 * 1024, // 10MB default
      allowedTypes = [],
      allowedExtensions = []
    } = options;

    const errors = [];

    // Check file size
    if (file.size > maxSize) {
      errors.push(`File size must be less than ${formatFileSize(maxSize)}`);
    }

    // Check file type
    if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
      errors.push(`File type ${file.type} is not allowed`);
    }

    // Check file extension
    if (allowedExtensions.length > 0) {
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!allowedExtensions.includes(extension)) {
        errors.push(`File extension .${extension} is not allowed`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Generate thumbnail for images
  generateThumbnail: async (file, options = {}) => {
    const { width = 200, height = 200, quality = 0.8 } = options;

    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = width;
        canvas.height = height;

        // Calculate aspect ratio
        const aspectRatio = img.width / img.height;
        let drawWidth = width;
        let drawHeight = height;

        if (aspectRatio > 1) {
          drawHeight = width / aspectRatio;
        } else {
          drawWidth = height * aspectRatio;
        }

        // Center the image
        const x = (width - drawWidth) / 2;
        const y = (height - drawHeight) / 2;

        ctx.drawImage(img, x, y, drawWidth, drawHeight);

        canvas.toBlob(resolve, 'image/jpeg', quality);
      };

      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  }
};

// Utility function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default fileUploadService;